// Setup › Company details on the SettingsPage pattern (BL-189): Setup's first page. Find a setting (any
// rule by a word, on the pages this person opens), Getting started (onboarding.status, and the next step's
// form), On your paperwork (what customers read from the company, and the terms version a signature
// records) and the departments (businessUnits.list), each with its customer text. As Company.tsx drew it
// in Blueprint, with nothing changed: the customer-text sheet and a department's open in a FormSheet.
// While the company mirrors its import source, a department's name and archiving are the source's, and so
// are the starter and the price book's steps (BL-088, BL-097), with the line. Riverstone keeps CompanyScreen.
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { RpcError } from '@flowology/client';
import { PARAGRAPH_FIELDS } from '@flowology/domain';
import {
  Button,
  Checkbox,
  Choice,
  ChoiceGroup,
  KeyValue,
  Link,
  Meter,
  Notice,
  RowList,
  SelectField,
  TextField,
} from '@flowology/ui-web/fl';
import {
  FormSheet,
  SettingFields,
  SettingRow,
  SettingsPage,
  SettingsSection,
} from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { canOpenIn, placesFor } from '../blueprint/places.ts';
import { dayText, todayIn } from '../books/model.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import {
  CUSTOMER_TEXT_FIELDS,
  paragraphPreview,
  paragraphProblem,
  textForm,
  textWrites,
  type TextForm,
} from '../settings/customer-text-model.ts';
import { findSetting, useRuleData } from '../settings/rule-data.ts';
import {
  BC_WEEK_LINE,
  findSettings,
  hoursAsMinutes,
  invitedWords,
  isBcRules,
  minutesAsHours,
  OVERTIME_PARTS,
  ruleSection,
  settingsRights,
  weekWarning,
  type Overtime,
  type RulePage,
  type SettingRow as Setting,
} from '../settings/settings-model.ts';
import {
  emptyUnitForm,
  NOTHING,
  setupRights,
  UNIT_RULES,
  unitDefaultsInput,
  unitInput,
  type Unit,
  type UnitForm,
} from '../settings/setup-model.ts';
import { SetupRuleRows } from '../settings/SetupRules.tsx';
import {
  nextStep,
  provinceFromTimeZone,
  subscriptionLine,
  type Step,
  type Subscription,
} from './account-model.ts';
import { billing } from './billing.ts';
import {
  firstDayOf,
  firstPeriod,
  PAY_EVERY,
  periodNote,
  saveWords,
  weekChoices,
  weekdayOf,
  type PayEvery,
} from './payroll-step-model.ts';

export const DETAILS_LEDE = 'Who you are on paper, your departments, and how you look to customers.';
const PAPER_LINE =
  'What customers read on estimates, invoices and messages, unless a department has its own (under Departments).';
const FIVE = 5;

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

const PROVINCES: [string, string][] = [
  ['AB', 'Alberta'],
  ['BC', 'British Columbia'],
  ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'],
  ['NS', 'Nova Scotia'],
  ['NT', 'Northwest Territories'],
  ['NU', 'Nunavut'],
  ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'],
  ['QC', 'Québec'],
  ['SK', 'Saskatchewan'],
  ['YT', 'Yukon'],
];
const ROLES = [
  { value: 'csr', label: 'Answers the phone and books jobs' },
  { value: 'dispatcher', label: 'Dispatches the techs' },
  { value: 'bookkeeper', label: 'Keeps the books' },
  { value: 'manager', label: 'Runs the office' },
];

export function CompanyDetailsPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  // by the permissions the person holds, whichever role gives them (QA-681)
  const owner = held.includes('billing.manage');
  const canSetUp = held.includes('settings.edit');
  const canEdit = setupRights(held).org;
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{ ...(crumb ? { crumb } : {}), title: 'Company details', lede: DETAILS_LEDE }}
          // Setup's first page: any rule, by a word, on the pages this person opens
          search={<FindSetting held={held} />}
        >
          <GettingStarted
            canSetUp={canSetUp}
            owner={owner}
            timeZone={nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
            onBook={() => nav.onView('book')}
            onSignIn={() => nav.onView('signin')}
          />
          <Paperwork canEdit={canEdit} held={held} />
          <Departments canEdit={canEdit} mirroring={mirroring} />
        </SettingsPage>
      </main>
    </div>
  );
}

function FindSetting({ held }: { held: readonly string[] }) {
  const [q, setQ] = useState('');
  // each Setup page this person opens, by its rail name and its link, as the rail draws them
  const [pages] = useState(() => {
    const m = new Map<string, { label: string; href: string }>();
    const setup = placesFor(canOpenIn(held), held).find((p) => p.id === 'setup');
    for (const g of setup?.groups ?? [])
      for (const x of g.pages)
        m.set(x.view === 'settings' && x.part ? x.part : x.view, {
          label: x.label,
          href: `#/${x.view}${x.part ? `/${x.part}` : ''}`,
        });
    return m;
  });
  const found = findSettings(q, (p: RulePage) => pages.has(p));
  const asked = q.trim().length > 1;
  return (
    <div role="search">
      <TextField
        label="Find a setting"
        type="search"
        value={q}
        placeholder="A word or two, like overtime or reminder"
        onChange={setQ}
        {...(asked && !found.length ? { description: 'No setting has those words. Try one word.' } : {})}
      />
      {asked && found.length > 0 && (
        <RowList
          label="Settings found"
          rows={found.map((f) => {
            const at = pages.get(f.page)!;
            return {
              id: f.key,
              name: (
                <Link href={at.href} onPress={() => findSetting(f.key)}>
                  {f.label}
                </Link>
              ),
              lines: [{ text: `${at.label} › ${f.section}` }],
            };
          })}
        />
      )}
    </div>
  );
}

function GettingStarted({
  canSetUp,
  owner,
  timeZone,
  onBook,
  onSignIn,
}: {
  canSetUp: boolean;
  owner: boolean;
  timeZone: string;
  onBook: () => void;
  onSignIn: () => void;
}) {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set());
  // The trial's end, for the owner (QA-336).
  const [trial, setTrial] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const load = useCallback(() => {
    void Promise.all([
      client.call<{ steps: Step[] }>('onboarding.status', {}),
      // Your own two-factor (QA-329): a step for the person, not the company's data.
      client.me<{ on: boolean; required: boolean }>('GET', '/me/2fa').catch(() => null),
    ])
      .then(([r, tfa]) => {
        setSteps([
          ...r.steps,
          ...(tfa
            ? [{ key: 'twoFactor', label: 'Two-factor sign-in for you', done: tfa.on, required: false }]
            : []),
        ]);
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
    if (owner)
      void billing<{ subscription: Subscription | null }>('GET', '/billing')
        .then((b) =>
          setTrial(b.subscription?.status === 'trialing' ? subscriptionLine(b.subscription, timeZone) : null),
        )
        .catch(() => setTrial(null));
  }, [owner, timeZone]);
  useEffect(load, [load]);
  const next = steps ? nextStep(steps, skipped) : null;
  const done = steps?.filter((s) => s.done).length ?? 0;
  // what's left first, in the list's order, five of them (DESIGN.md §3); every step once opened in full
  const left = steps?.filter((s) => !s.done) ?? [];
  const shown = steps ? (all ? steps : (left.length ? left : steps).slice(0, FIVE)) : [];
  return (
    <SettingsSection
      title={steps && !next ? 'You’re set up' : 'Getting started'}
      {...(steps
        ? {
            line: `${done} of ${steps.length} done.${next ? ' Each step takes a minute; you can change everything later in settings.' : ''}`,
          }
        : {})}
      status={steps ? 'ready' : error ? 'failed' : 'loading'}
      failedText={error ?? undefined}
      onRetry={load}
    >
      {steps && (
        <>
          <Meter value={done} max={steps.length} label={`${done} of ${steps.length} steps done`} />
          {trial && <Notice title={trial} />}
          {next && (
            <NextStep
              step={next}
              canSetUp={canSetUp}
              owner={owner}
              timeZone={timeZone}
              onBook={onBook}
              onSignIn={onSignIn}
              onDone={load}
              onSkip={() => setSkipped(new Set([...skipped, next.key]))}
            />
          )}
          <RowList
            label="Getting started"
            rows={shown.map((s) => ({
              id: s.key,
              name: s.label,
              ...(s.required ? {} : { after: { text: ' (optional)' } }),
              ...(s.done
                ? { end: { word: 'Done', tone: 'muted' as const } }
                : s === next
                  ? { end: { word: 'Now' } }
                  : {}),
            }))}
          />
          {shown.length < steps.length && <Link onPress={() => setAll(true)}>Show all {steps.length}</Link>}
        </>
      )}
    </SettingsSection>
  );
}

/** What the next step asks, as a setting: its name and words on the left, its form on the right. */
function NextStep({
  step,
  canSetUp,
  owner,
  timeZone,
  onBook,
  onSignIn,
  onDone,
  onSkip,
}: {
  step: Step;
  canSetUp: boolean;
  owner: boolean;
  timeZone: string;
  onBook: () => void;
  onSignIn: () => void;
  onDone: () => void;
  onSkip: () => void;
}) {
  const mirroring = useMirror();
  // Enter in one of the step's boxes sends it, as the form did
  const send = useRef<(() => void) | null>(null);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement && send.current) {
      e.preventDefault();
      send.current();
    }
  };
  const skip = step.required ? null : <Link onPress={onSkip}>Skip for now</Link>;
  const row = (help: string | null, control: ReactNode, wide = false) => (
    <SettingRow
      name={`Now: ${step.label}`}
      {...(help ? { help } : {})}
      {...(wide ? { isWide: true } : {})}
      onControlKeyDown={onKey}
      control={
        <>
          {control}
          {skip}
        </>
      }
    />
  );
  if (step.key === 'firstJob')
    return row(
      'Book your first job: a customer, a time and a technician.',
      <Button variant="primary" onPress={onBook}>
        Book a job
      </Button>,
    );
  if (step.key === 'twoFactor')
    return row(
      'A second step when you sign in, so a stolen password isn’t enough.',
      <Button variant="primary" onPress={onSignIn}>
        Set up two-factor
      </Button>,
    );
  if (step.key === 'texting')
    return row(
      'Confirmations, “on the way” texts and pay links go from your company’s own number. Flowology connects it for you: ask us, and we’ll set up your number.',
      null,
    );
  if (step.key === 'payments')
    return row(
      'Taking cards on site needs your card processor and a terminal. Flowology connects them for you: ask us when you’re ready.',
      null,
    );
  // the pay schedule and payroll's rules are the owner's (payroll.send, payroll.prepare)
  if (step.key === 'payroll')
    return owner
      ? // its many fields go under its words, the whole width
        row(PAYROLL_LINE, <PayrollStep timeZone={timeZone} onDone={onDone} send={send} />, true)
      : row('Ask the owner to finish this step. Hours and timesheets work in the meantime.', null);
  if (!canSetUp) return row('Ask the owner or a manager to finish this step.', null);
  // a price book item and the starter (departments, job types, tax) are the import source's while the
  // company mirrors it (BL-088)
  if (mirroring && (step.key === 'pricebook' || step.key === 'tax')) return row(MIRROR_LINE, null);
  return row(
    STEP_LINES[step.key] ?? null,
    <StepForm step={step} timeZone={timeZone} onDone={onDone} send={send} />,
  );
}

const STARTER_LINE =
  'We set up a department, three kinds of job (service call, estimate, maintenance) and your province’s sales tax. Only what you don’t have yet.';
const STEP_LINES: Record<string, string> = {
  businessUnit: STARTER_LINE,
  jobTypes: STARTER_LINE,
  tax: STARTER_LINE,
};
const PAYROLL_LINE =
  'How often you pay, the week overtime is counted in, and who runs payroll. Hours and timesheets work without it; this starts pay periods and locking. You can change it all later in Setup, Payroll.';

type Send = { current: (() => void) | null };

function StepForm({
  step,
  timeZone,
  onDone,
  send,
}: {
  step: Step;
  timeZone: string;
  onDone: () => void;
  send: Send;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [province, setProvince] = useState(() => provinceFromTimeZone(timeZone));
  const [role, setRole] = useState('csr');
  // What an invitation sent (or what to tell them): kept over the next step (QA-322).
  const [note, setNote] = useState<string | null>(null);
  // an invitation that hands out access asks a fresh second factor (QA-685): these never do, but the
  // code box is there if the server asks
  const { run, ask } = useStepUp();
  useEffect(() => {
    setA('');
    setB('');
    setError(null);
  }, [step.key]);
  const invite = (roleKey: string) =>
    run(() =>
      client.call<{ emailed: boolean; signInAt: string | null }>('people.invite', {
        email: b.trim(),
        displayName: a,
        roleKeys: [roleKey],
      }),
    ).then((r) => setNote(invitedWords(r, a.trim(), b.trim())));
  const missing =
    step.key === 'profile'
      ? !a.trim()
      : step.key === 'pricebook'
        ? !a.trim() || !/^[0-9]+(\.[0-9]{1,2})?$/.test(b)
        : step.key === 'invites'
          ? !a.trim() || !b.trim()
          : step.key === 'team'
            ? !a.trim()
            : false;
  const submit = () => {
    if (busy || missing) return;
    setBusy(true);
    setError(null);
    const call =
      step.key === 'profile'
        ? client.call('settings.set', { key: 'company.phone', value: a })
        : step.key === 'pricebook'
          ? client.call('pricebook.createItem', {
              type: 'service',
              code: a
                .toUpperCase()
                .replace(/[^A-Z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 20),
              name: a,
              priceCents: Math.round(Number(b) * 100),
            })
          : step.key === 'invites'
            ? invite(role)
            : step.key === 'team'
              ? b.trim()
                ? // With their email they're invited to sign in, as a technician (people.invite).
                  invite('technician')
                : client.call('staff.create', { displayName: a, isTechnician: true })
              : client.call('onboarding.applyStarter', { province });
    void call
      .then(onDone)
      .catch((err: unknown) => setError(message(err)))
      .finally(() => setBusy(false));
  };
  send.current = submit;
  return (
    <>
      <SettingFields label={step.label} error={error}>
        {step.key === 'profile' && (
          <TextField
            label="The phone number your customers call"
            type="tel"
            isRequired
            value={a}
            onChange={setA}
          />
        )}
        {(step.key === 'businessUnit' || step.key === 'jobTypes' || step.key === 'tax') && (
          <SelectField
            label="Your province or territory"
            value={province}
            options={PROVINCES.map(([value, label]) => ({ value, label }))}
            onChange={setProvince}
          />
        )}
        {step.key === 'pricebook' && (
          <>
            <TextField label="A service you sell" isRequired value={a} onChange={setA} />
            <TextField
              label="Its price, in dollars"
              inputMode="decimal"
              width="number"
              isRequired
              value={b}
              onChange={setB}
            />
          </>
        )}
        {note && <Notice tone="done" title={note} />}
        {step.key === 'team' && (
          <>
            <TextField label="A technician’s name" isRequired value={a} onChange={setA} />
            <TextField
              label="Their email, to invite them to sign in (optional)"
              description="They sign in on the iPad with it. You can invite them later in Settings, People."
              type="email"
              autoComplete="off"
              value={b}
              onChange={setB}
            />
          </>
        )}
        {step.key === 'invites' && (
          <>
            <TextField label="Their name" isRequired value={a} onChange={setA} />
            <TextField
              label="Their email, for the invitation"
              type="email"
              autoComplete="off"
              isRequired
              value={b}
              onChange={setB}
            />
            <SelectField label="What they do" value={role} options={ROLES} onChange={setRole} />
          </>
        )}
      </SettingFields>
      <Button variant="primary" isDisabled={busy || missing} onPress={submit}>
        {step.key === 'profile'
          ? 'Save the phone number'
          : step.key === 'pricebook'
            ? 'Add the price'
            : step.key === 'invites'
              ? 'Send the invitation'
              : step.key === 'team'
                ? b.trim()
                  ? 'Add and invite the technician'
                  : 'Add the technician'
                : 'Set up the basics'}
      </Button>
      {ask}
    </>
  );
}

/**
 * Getting started's "How you'll pay your team" (BL-172): how often and the first period (as Payroll's
 * own first period, payPeriods.create), the overtime week (Sunday first under BC's rules, a plain
 * warning for another day, never a refusal), the overtime rules (BC's by default) and who runs payroll,
 * "None yet" included. Saving the provider is what marks the step done when no period opens (a company
 * mirroring its old system opens its periods after the switch). Time tracking never waits on it.
 */
function PayrollStep({ timeZone, onDone, send }: { timeZone: string; onDone: () => void; send: Send }) {
  const mirroring = useMirror();
  const [now, setNow] = useState<{ week: number; overtime: Overtime } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [every, setEvery] = useState<PayEvery>(14);
  // the first day follows the week until it's typed
  const [firstDay, setFirstDay] = useState<string | null>(null);
  const [week, setWeek] = useState<number | null>(null);
  const [hours, setHours] = useState<Record<string, string> | null>(null);
  const [named, setNamed] = useState(false);
  const [provider, setProvider] = useState('');
  // a period opened before a later save failed: not opened twice
  const [opened, setOpened] = useState(false);
  const load = useCallback(() => {
    setLoadError(null);
    void Promise.all([
      client.call<{ value: unknown }>('settings.get', { key: 'payroll.weekStartsOn' }),
      client.call<{ value: unknown }>('settings.get', { key: 'payroll.overtime' }),
    ])
      .then(([w, o]) =>
        setNow({ week: typeof w.value === 'number' ? w.value : 0, overtime: o.value as Overtime }),
      )
      .catch((e: unknown) => setLoadError(message(e)));
  }, []);
  useEffect(load, [load]);
  if (!now)
    return loadError ? (
      <p role="alert">
        {loadError} <Link onPress={load}>Try again</Link>
      </p>
    ) : null;
  const typed =
    hours ?? Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, minutesAsHours(now.overtime[k])]));
  const parsed = Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, hoursAsMinutes(typed[k] ?? '')]));
  const overtime = Object.values(parsed).some((m) => m === null) ? null : (parsed as unknown as Overtime);
  const bc = isBcRules(overtime ?? now.overtime);
  const w = week ?? now.week;
  const start = firstDay ?? firstDayOf(todayIn(timeZone), w, every);
  const period = mirroring || opened ? null : firstPeriod(start, every);
  const warning = weekWarning(w, bc);
  const note = period ? periodNote(start, w) : null;
  const submit = async () => {
    if (busy) return;
    if (!overtime) return setError('Enter each as hours, like 8 or 7.5.');
    if (named && !provider.trim()) return setError('Name your payroll provider, or choose None yet.');
    setBusy(true);
    setError(null);
    try {
      if (w !== now.week) await client.call('settings.set', { key: 'payroll.weekStartsOn', value: w });
      if (JSON.stringify(overtime) !== JSON.stringify(now.overtime))
        await client.call('settings.set', { key: 'payroll.overtime', value: overtime });
      if (period) {
        await client.call('payPeriods.create', period);
        setOpened(true);
      }
      await client.call('settings.set', { key: 'payroll.provider', value: named ? provider.trim() : '' });
      onDone();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  send.current = () => void submit();
  return (
    <>
      <SettingFields label="How you’ll pay your team" error={error}>
        {mirroring ? (
          <p>Your first pay period in Flowology opens after the switch.</p>
        ) : opened ? null : (
          <>
            <ChoiceGroup
              label="How often you pay"
              selectionMode="single"
              value={String(every)}
              onChange={(v) => setEvery(Number(v) as PayEvery)}
            >
              {PAY_EVERY.map(([n, label]) => (
                <Choice key={n} value={String(n)}>
                  {label}
                </Choice>
              ))}
            </ChoiceGroup>
            <TextField
              label="The first pay period starts"
              type="date"
              isRequired
              value={start}
              onChange={(v) => setFirstDay(v || null)}
              {...(period
                ? {
                    description: `${dayText(period.startDate)} to ${dayText(period.endDate)}, paid ${dayText(period.payDate)}.`,
                  }
                : {})}
            />
          </>
        )}
        <SelectField
          label="The overtime week starts on"
          value={String(w)}
          options={weekChoices(bc, period ? weekdayOf(start) : null).map(([d, name]) => ({
            value: String(d),
            label: name,
          }))}
          onChange={(v) => setWeek(Number(v))}
          {...(warning || bc || note
            ? {
                description: [warning ?? (bc ? BC_WEEK_LINE : null), note].filter(Boolean).join(' '),
              }
            : {})}
        />
        {/* one label over the three boxes, stacked, each box's words after it */}
        <SettingFields label="Overtime starts after">
          {OVERTIME_PARTS.map(([k, label], i) => (
            <TextField
              key={k}
              label={i ? label : 'Overtime starts after'}
              {...(i ? { hideLabel: true } : {})}
              {...(i === OVERTIME_PARTS.length - 1
                ? { description: 'BC’s: 8 hours a day, double time after 12, and 40 hours a week.' }
                : {})}
              inputMode="decimal"
              width="number"
              unit={label}
              value={typed[k] ?? ''}
              onChange={(v) => setHours({ ...typed, [k]: v })}
            />
          ))}
        </SettingFields>
        <ChoiceGroup
          label="Who runs your payroll"
          selectionMode="single"
          value={named ? 'provider' : 'none'}
          onChange={(v) => setNamed(v === 'provider')}
        >
          <Choice value="none">None yet</Choice>
          <Choice value="provider">A payroll provider</Choice>
        </ChoiceGroup>
        {named && (
          <TextField
            label="The provider’s name"
            isRequired
            maxLength={100}
            value={provider}
            onChange={setProvider}
          />
        )}
        <p>{saveWords(period, named ? provider : '')}</p>
      </SettingFields>
      <Button variant="primary" isDisabled={busy} onPress={() => void submit()}>
        {busy ? 'Saving…' : 'Save how you pay'}
      </Button>
    </>
  );
}

/**
 * On your paperwork (the setup audit): what customers read from the company, its name, phone, email,
 * address and licence and the invoice's words, with one way to change it, the customer-text sheet a
 * department's own values sit beside; then the terms version a signature records.
 */
function Paperwork({ canEdit, held }: { canEdit: boolean; held: readonly string[] }) {
  // settings.list once: the values on the paperwork and the terms version's row
  const rules = useRuleData();
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const rows = rules.rows;
  const form = rows ? textForm(rows, null) : null;
  const value = (key: string) => form?.[key]?.text.trim() ?? '';
  return (
    <>
      <SettingsSection
        title="On your paperwork"
        line={PAPER_LINE}
        action={<Link onPress={() => setOpen(true)}>{canEdit ? 'Change' : 'Open'}</Link>}
        {...(said ? { note: said } : {})}
        status={form ? 'ready' : rules.error ? 'failed' : 'loading'}
        failedText={rules.error ?? undefined}
        onRetry={() => void rules.load()}
      >
        {form && (
          <KeyValue
            label="On your paperwork"
            items={CUSTOMER_TEXT_FIELDS.map((f) => ({
              label: f.label,
              value:
                f.kind === 'paragraph'
                  ? value(f.key)
                    ? 'Set'
                    : 'Not set'
                  : value(f.key).replaceAll('\n', ', ') || 'Not set',
            }))}
          />
        )}
        {/* the terms version a signature records, beside the words signed */}
        <SetupRuleRows
          rules={ruleSection('company.paper')?.rules ?? []}
          data={rules}
          rights={settingsRights(held)}
        />
      </SettingsSection>
      {open && (
        <CustomerTextSheet
          unit={null}
          canEdit={canEdit}
          onSaved={(words) => {
            setSaid(words);
            void rules.load();
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * What customers read from the company or one department (BL-052): its name, phone, email, address
 * and licence, the invoice's header and footer, and the paragraphs a customer signs. A department's
 * field is "Same as the company" until it has its own. Each change is one settings.set or
 * settings.clear, so a department given back to the company follows the company's later edits.
 */
function CustomerTextSheet({
  unit,
  canEdit,
  onSaved,
  onClose,
}: {
  unit: Unit | null;
  canEdit: boolean;
  onSaved: (words: string) => void;
  onClose: () => void;
}) {
  const [was, setWas] = useState<TextForm | null>(null);
  const [f, setF] = useState<TextForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const unitId = unit?.id ?? null;
  const load = useCallback(() => {
    client
      .call<{ settings: Setting[] }>('settings.list', {})
      .then((r) => {
        const form = textForm(r.settings, unitId);
        setWas(form);
        setF(form);
        setLoadError(null);
      })
      .catch((e: unknown) => setLoadError(refusal(e)));
  }, [unitId]);
  useEffect(load, [load]);
  const who = unit ? unit.name : 'the company';
  const companyName = (f?.['company.officialName']?.text || unit?.name) ?? '';
  const set = (key: string, patch: Partial<TextForm[string]>) => {
    if (f) setF({ ...f, [key]: { ...f[key]!, ...patch } });
  };
  return (
    <FormSheet
      isOpen
      title={unit ? `Customer text: ${unit.name}` : 'The company’s customer text'}
      line={`${
        unit
          ? `What customers of ${unit.name} see on its estimates, invoices and messages. A field left “Same as the company” follows the company’s.`
          : 'What customers see on estimates, invoices and messages, unless a department has its own.'
      } A signed paragraph is kept with each signature exactly as the customer read it.`}
      submitLabel="Save"
      isReadOnly={!canEdit}
      isDirty={!!f && JSON.stringify(f) !== JSON.stringify(was)}
      isSubmitDisabled={!f}
      onClose={onClose}
      onSubmit={async () => {
        if (!was || !f) return;
        const built = textWrites(was, f, unitId);
        if ('error' in built) throw new Error(built.error);
        if (!built.writes.length) throw new Error('Nothing has changed.');
        try {
          for (const w of built.writes) await client.call(w.action, w.input);
        } catch (e) {
          // the ones before it are saved: read again, so the form shows what is
          load();
          throw new Error(refusal(e), { cause: e });
        }
        onSaved(`Saved: what customers read from ${who}.`);
      }}
    >
      {!f ? (
        loadError ? (
          <p role="alert">
            {loadError} <Link onPress={load}>Try again</Link>
          </p>
        ) : null
      ) : (
        CUSTOMER_TEXT_FIELDS.map((t) => {
          const v = f[t.key]!;
          const editing = !unit || v.own;
          const problem = t.paragraph && editing ? paragraphProblem(v.text, t.paragraph) : null;
          const fields = t.paragraph
            ? `Fields: ${PARAGRAPH_FIELDS[t.paragraph].map(([k, what]) => `{${k}} ${what}`).join(', ')}.`
            : null;
          const description = !editing ? (
            `Same as the company${v.company ? `: ${v.company.replace(/\n/g, ' · ')}` : ' (none)'}`
          ) : t.help || fields ? (
            <>
              {t.help}
              {t.help && fields ? ' ' : null}
              {fields}
              {t.paragraph ? (
                <span aria-label={`${t.label}, as a customer reads it`}>
                  <br />
                  {paragraphPreview(v.text, companyName)}
                </span>
              ) : null}
            </>
          ) : undefined;
          return (
            <Fragment key={t.key}>
              <TextField
                label={t.label}
                value={editing ? v.text : v.company}
                isReadOnly={!editing || !canEdit}
                {...(t.kind === 'line' ? {} : { multiline: true })}
                {...(t.placeholder ? { placeholder: t.placeholder } : {})}
                {...(description ? { description } : {})}
                {...(problem ? { error: problem } : {})}
                onChange={(text) => set(t.key, { text })}
              />
              {unit && (
                <Checkbox
                  aria-label={`Own for ${unit.name}: ${t.label}`}
                  isSelected={v.own}
                  isDisabled={!canEdit}
                  onChange={(own) => set(t.key, { own, text: own ? v.text : v.company })}
                >
                  Own for {unit.name}
                </Checkbox>
              )}
            </Fragment>
          );
        })
      )}
    </FormSheet>
  );
}

const statusOf = (rows: readonly unknown[] | null, error: string | null) =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

function Departments({ canEdit, mirroring }: { canEdit: boolean; mirroring: boolean }) {
  const [units, setUnits] = useState<Unit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unit, setUnit] = useState<Unit | 'new' | null>(null);
  // whose customer text is open (BL-052)
  const [text, setText] = useState<Unit | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    client
      .call<{ businessUnits: Unit[] }>('businessUnits.list', { includeInactive: true })
      .then((r) => {
        setUnits(r.businessUnits);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  const note = [
    ...(mirroring && canEdit
      ? [`Departments’ names: ${MIRROR_LINE} A department’s customer text stays yours.`]
      : []),
    ...(!canEdit ? ['Only the owner or a manager changes these.'] : []),
    ...(said ? [said] : []),
  ].join(' ');
  return (
    <>
      <SettingsSection
        title="Departments"
        {...(canEdit && !mirroring
          ? {
              action: (
                <Button variant="primary" onPress={() => setUnit('new')}>
                  Add a department
                </Button>
              ),
            }
          : {})}
        {...(note ? { note } : {})}
        status={statusOf(units, error)}
        emptyText="No departments yet. Add your first, for example Plumbing."
        failedText={error ?? undefined}
        onRetry={load}
      >
        {units && (
          <RowList
            label="Departments"
            rows={units.map((u) => ({
              id: u.id,
              name: u.name,
              onOpen: () => setUnit(u),
              openLabel: `${canEdit ? 'Edit' : 'Open'} ${u.name}`,
              after: {
                text: ` · ${u.defaultInvoiceMode === 'flat_rate' ? 'Flat rate' : 'Time and materials'}`,
                size: 'sm' as const,
              },
              ...(u.active ? {} : { word: { text: 'Archived' } }),
              actions: [
                {
                  id: 'text',
                  text: 'Customer text',
                  label: `Customer text for ${u.name}`,
                  onPress: () => setText(u),
                },
              ],
            }))}
          />
        )}
      </SettingsSection>
      {unit && (
        <UnitSheet
          key={unit === 'new' ? 'new' : unit.id}
          unit={unit === 'new' ? null : unit}
          canEdit={canEdit}
          ownOnly={mirroring}
          onSaved={(u) => {
            setSaid(`${u} saved.`);
            load();
          }}
          onClose={() => setUnit(null)}
        />
      )}
      {text && (
        <CustomerTextSheet unit={text} canEdit={canEdit} onSaved={setSaid} onClose={() => setText(null)} />
      )}
    </>
  );
}

function UnitSheet({
  unit,
  canEdit,
  ownOnly,
  onSaved,
  onClose,
}: {
  unit: Unit | null;
  canEdit: boolean;
  /** While the company mirrors its import source: its defaults only, never its name or archiving (BL-097). */
  ownOnly: boolean;
  onSaved: (name: string) => void;
  onClose: () => void;
}) {
  const [first] = useState<UnitForm>(() => (unit ? { ...unit } : emptyUnitForm()));
  const [f, setF] = useState(first);
  // "The customer signs to accept the finished work" is the setting invoices.signatureRequired for
  // this department (BL-052): the company's until it's changed here
  const [sig, setSig] = useState<{ was: boolean; now: boolean } | null>(null);
  const unitId = unit?.id;
  useEffect(() => {
    void client
      .call<{ value: boolean }>('settings.get', {
        key: 'invoices.signatureRequired',
        scope: unitId ? { businessUnitId: unitId } : {},
      })
      .then((r) => setSig({ was: r.value, now: r.value }))
      .catch(() => setSig(null));
  }, [unitId]);
  const save = useSaveKey(JSON.stringify([f, sig?.now]));
  const priced = (mode: UnitForm['defaultInvoiceMode']) =>
    mode === 'flat_rate' ? 'Flat rate (from the price book)' : 'Time and materials';
  return (
    <FormSheet
      isOpen
      title={unit ? unit.name : 'Add a department'}
      {...(ownOnly && canEdit ? { line: `Its name and archiving: ${MIRROR_LINE}` } : {})}
      submitLabel={unit ? 'Save' : 'Add department'}
      isReadOnly={!canEdit}
      {...(!canEdit
        ? { note: 'Only the owner or a manager changes these.' }
        : unit
          ? { note: 'A change applies to new jobs; jobs already booked keep theirs.' }
          : {})}
      isDirty={JSON.stringify(f) !== JSON.stringify(first) || (!!sig && sig.now !== sig.was)}
      isSubmitDisabled={!f.name.trim()}
      onClose={onClose}
      onSubmit={async () => {
        const built = ownOnly && unit ? unitDefaultsInput(unit, f) : unitInput(unit, f);
        const sigChanged = !!sig && sig.now !== sig.was;
        let id = unit?.id;
        try {
          if (!(sigChanged && 'error' in built && built.error === NOTHING)) {
            if ('error' in built) throw new Error(built.error);
            const r = await client.call<Unit>(
              ownOnly ? 'businessUnits.setDefaults' : unit ? 'businessUnits.update' : 'businessUnits.create',
              built.input,
              { idempotencyKey: save.key },
            );
            id = r.id;
          }
          if (sigChanged && id)
            await client.call(
              'settings.set',
              { key: 'invoices.signatureRequired', scopeType: 'business_unit', scopeId: id, value: sig.now },
              { idempotencyKey: `${save.key}:signature` },
            );
        } catch (e) {
          throw new Error(e instanceof RpcError ? refusal(e) : message(e), { cause: e });
        }
        save.renew();
        onSaved(f.name.trim());
      }}
    >
      {!canEdit && unit ? (
        <KeyValue
          label={unit.name}
          isStackedOnPhone
          items={[
            { label: 'How its jobs are priced', value: priced(unit.defaultInvoiceMode) },
            ...UNIT_RULES.map(([k, label]) => ({ label, value: unit[k] ? 'Yes' : 'No' })),
            ...(unit.active ? [] : [{ label: 'Archived', value: 'No new jobs, history kept' }]),
          ]}
        />
      ) : (
        <>
          <TextField
            label="Name"
            value={f.name}
            isReadOnly={ownOnly}
            autoFocus
            onChange={(name) => setF({ ...f, name })}
          />
          <SelectField
            label="How its jobs are priced"
            value={f.defaultInvoiceMode}
            options={[
              { value: 'flat_rate', label: priced('flat_rate') },
              { value: 'time_and_materials', label: priced('time_and_materials') },
            ]}
            onChange={(v) => setF({ ...f, defaultInvoiceMode: v as UnitForm['defaultInvoiceMode'] })}
          />
          {UNIT_RULES.map(([k, label]) => (
            <Checkbox key={k} isSelected={f[k]} onChange={(on) => setF({ ...f, [k]: on })}>
              {label}
            </Checkbox>
          ))}
          {sig && (
            <Checkbox isSelected={sig.now} onChange={(now) => setSig({ ...sig, now })}>
              The customer signs to accept the finished work
            </Checkbox>
          )}
          {unit && !ownOnly && (
            <Checkbox isSelected={!f.active} onChange={(off) => setF({ ...f, active: !off })}>
              Archived: no new jobs, history kept
            </Checkbox>
          )}
        </>
      )}
    </FormSheet>
  );
}
